import { useTranslation } from 'react-i18next';
import { toast, useServerResponseTranslation } from 'dash-ui';

import { useAddPropertyToTrackMutation } from 'gql/generated.graphql';
import { GTMEventsEnum } from 'common/google-tag-manager/constants';
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from 'common/google-tag-manager/helpers';
import { AccountModel } from 'dash-common';
import {
  buildAddress,
  buildBuiltRange,
  buildRangeString,
} from 'common/helpers';

export const useAddPropertyToTrack = ({
  onCompleted,
}: {
  onCompleted?: () => void;
} = {}) => {
  const { t } = useTranslation('pages');
  const [addPropertyToTrack, { loading }] = useAddPropertyToTrackMutation();
  const translateServerError = useServerResponseTranslation();

  const handleAddPropertyToTrack = (
    property: Record<string, any>,
    account: AccountModel | null
  ) => {
    addPropertyToTrack({
      variables: { propertyId: property._id },
      onCompleted: () => {
        if (account) {
          createGTMEvent({
            dataLayer: {
              event: GTMEventsEnum.PROPERTY_ADDED_TO_TRACKING,
              user_id: getGoogleTagManagerUserId(account.email),
              property_name: property.name,
              price_range: buildRangeString(property.priceRange),
              property_type: property.buildingTypes,
              number_of_bedrooms: buildRangeString(
                property.averageBedroomsNumberValue
              ),
              number_of_bathrooms: buildRangeString(
                property.averageBathroomsNumberValue
              ),
              number_of_units: property.numberOfUnits,
              appeared_date: property.createdAt.split('T')[0],
              features_and_amenities: property.amenityNames
                .map(({ name }: { name: string }) => name.trim())
                .join(', '),
              completion_year: property.yearBuilt,
              project_status: property.projectStatus,
              year_built: buildBuiltRange({
                startDate: property.constructionStartDate,
                endDate: property.completionDate || `${property.yearBuilt}`,
                yearOnly: true,
              }),
              scheduled_construction: buildBuiltRange({
                startDate: property.constructionStartDate,
                endDate: property.completionDate || `${property.yearBuilt}`,
              }),
              marketArea: property.marketArea,
              address: buildAddress(property.location.address),
            },
          });
        }
        if (onCompleted) {
          onCompleted();
        }
        toast.success(t('propertyPage.addToTrackToast'));
      },
      onError: (error) => {
        if (error.message.includes(property._id!)) {
          return toast.error(t('propertyPage.propertyAlreadyTracked'));
        }
        toast.error(translateServerError(error));
      },
    });
  };

  return { handleAddPropertyToTrack, loading };
};
